import {
    ChangeDetectionStrategy,
    Component,
    computed,
    Inject,
    OnDestroy,
    OnInit,
    Signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import {
    combineLatest,
    distinctUntilChanged,
    Observable,
    Subscription,
} from "rxjs";
import { filter, map, withLatestFrom } from "rxjs/operators";

import { Shop } from "@hermes/api-model-shop";
import { Context, INFRA_SETTINGS, LOCALE, WINDOW } from "@hermes/app-core";
import { InfraSettings } from "@hermes/env-infra";
import { SeoLinks } from "@hermes/fragments/seo-links";
import {
    DEFAULT_SECTIONS_TO_DISPLAY,
    SectionName,
    ShopInfoEvent,
} from "@hermes/fragments/shop";
import { Locale } from "@hermes/locale";
import {
    APPOINTMENT_IN_STORE,
    FeatureFlagFacade,
} from "@hermes/states/flipper";
import { selectPreviousUrl } from "@hermes/states/router";
import {
    CurrentShopLocation,
    SHOP_LOCATIONS_CITY_MODE,
    SHOP_LOCATIONS_COUNTRY_MODE,
    SHOP_LOCATIONS_DEFAULT_MODE,
    ShopLocationsFacade,
} from "@hermes/states/shop-locations";
import {
    SelectedLocationCoordinates,
    SelectedLocationState,
    ShopLocatorFacade,
    ShopLocatorMode,
} from "@hermes/states/shop-locator";
import {
    AnalyticsService,
    GTMEventData,
    GTMEventPromotionData,
} from "@hermes/fragments/analytics";

import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

import { ShopLocatorAnalyticsService } from "../../analytics/services/shop-locator-analytics.service";
import { PromotionClickShopLocatorEvent } from "../../events/promotion-click-shop-locator.event";
import { ShopLocatorEvent } from "../../events/shop-locator.event";
import { generateSeoLinks } from "../../helpers/link.helper";
import { ShopLocatorPageHeadService } from "../../services/shop-locator-page-head.service";

@Component({
    selector: "h-shop-locator-page",
    templateUrl: "./shop-locator-page.component.html",
    styleUrls: ["./shop-locator-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ShopLocatorPageComponent implements OnInit, OnDestroy {
    public sectionsToDisplay: Signal<Set<SectionName>> = computed(() =>
        this.shouldDisplayAppointmentButton()
            ? new Set<SectionName>([
                  ...DEFAULT_SECTIONS_TO_DISPLAY,
                  "appointmentInStore",
              ])
            : DEFAULT_SECTIONS_TO_DISPLAY,
    );

    public displayBackButton: Signal<boolean | undefined> = toSignal(
        this.store
            .select(selectPreviousUrl)
            .pipe(map((url) => url !== undefined)),
    );

    public pageTitle!: string;

    public isMobile$: Observable<boolean> = this.breakpointService.isMobile$;

    public isNavigationBarDisplayed$: Observable<boolean> =
        this.shopLocatorFacade.isANewSearch$.pipe(
            map((isANewSearch) => !isANewSearch),
        );

    public seoLinks$: Observable<SeoLinks> =
        this.shopLocationsFacade.mode$.pipe(
            withLatestFrom(
                this.isNavigationBarDisplayed$,
                this.shopLocationsFacade.currentLocation$,
                this.shopLocationsFacade.countries$,
                this.shopLocationsFacade.cities$,
            ),
            filter(
                ([mode, isActivated]) =>
                    isActivated &&
                    (mode === SHOP_LOCATIONS_DEFAULT_MODE ||
                        mode === SHOP_LOCATIONS_COUNTRY_MODE ||
                        mode === SHOP_LOCATIONS_CITY_MODE),
            ),
            map(([_mode, _isActivated, currentLocation, countries, cities]) =>
                generateSeoLinks(
                    {
                        base: this.infraSettings.frontend,
                        prefix: this.locale.urlPrefix,
                    },
                    {
                        current: currentLocation,
                        countries,
                        cities,
                    },
                ),
            ),
        );

    private shouldDisplayAppointmentButton = toSignal(
        this.featureFlagFacade.isActivated(APPOINTMENT_IN_STORE),
        { initialValue: false },
    );

    private subscription = new Subscription();

    constructor(
        public shopLocatorFacade: ShopLocatorFacade,
        public context: Context,
        private store: Store,
        private shopLocatorAnalyticsService: ShopLocatorAnalyticsService,
        private analyticsService: AnalyticsService,
        private shopLocationsFacade: ShopLocationsFacade,
        public featureFlagFacade: FeatureFlagFacade,
        private shopLocatorPageHeadService: ShopLocatorPageHeadService,
        private breakpointService: BreakpointService,
        @Inject(WINDOW) private window: Window,
        @Inject(LOCALE) private locale: Locale,
        @Inject(INFRA_SETTINGS) private infraSettings: InfraSettings,
        private activeRoute: ActivatedRoute,
    ) {}

    public ngOnInit(): void {
        /**
         * Neccessary to listen params
         * when switching from a country to another country
         * OR from a city to another city
         * in order to reload the component
         */
        this.subscription.add(
            combineLatest([this.activeRoute.params, this.isMobile$]).subscribe(
                ([_, isMobile]) => {
                    this.shopLocatorFacade.setMode(
                        isMobile ? ShopLocatorMode.NoMap : ShopLocatorMode.Map,
                    );
                },
            ),
        );

        this.subscription.add(
            this.shopLocationsFacade.currentLocation$
                .pipe(
                    distinctUntilChanged((previous, current) =>
                        this.areLocationsSimilarEnoughToNotTriggerSubscription(
                            previous,
                            current,
                        ),
                    ),
                )
                .subscribe((currentLocation) => {
                    this.shopLocatorPageHeadService.initHeadTag(
                        currentLocation,
                    );

                    this.managePageTitle(currentLocation);
                }),
        );

        if (this.context.isInBrowserMode()) {
            this.manageAnalytics();
        }
    }

    /**
     * Event when the user clicks the back button.
     * It will redirect to the previous url
     */
    public handleGoBack(): void {
        this.window.history.back();
    }

    public ngOnDestroy(): void {
        if (this.context.isInBrowserMode()) {
            this.shopLocatorPageHeadService.removeHeadTags();
        }
        this.subscription.unsubscribe();
    }

    public geolocationButtonSelected(search: string): void {
        this.analyticsService.sendData(
            new ShopLocatorEvent({
                eventaction: "Search - Use my location",
                eventlabel: search,
            }),
        );
    }

    public clickedShop(shop: Shop): void {
        this.analyticsService.sendData(
            new ShopLocatorEvent({
                eventaction: "Map view click marker",
                eventlabel: shop.shortTitle,
            }),
        );
    }

    public shopInfoEvents($event: {
        event: ShopInfoEvent;
        shop: Shop;
        shopIndex: number;
    }): void {
        const eventMapping: Partial<
            Record<
                ShopInfoEvent,
                PromotionClickShopLocatorEvent | ShopLocatorEvent
            >
        > = {
            direction: new ShopLocatorEvent({
                eventaction: "Get_Directions",
                eventlabel: $event.shop.shortTitle,
                eventvalue: ` ${$event.shopIndex}`,
            }),
            more_details: new ShopLocatorEvent({
                eventaction: "More information",
                eventlabel: $event.shop.shortTitle,
                eventvalue: ` ${$event.shopIndex}`,
            }),
            phone: new ShopLocatorEvent({
                eventaction: "Phone Call",
                eventlabel: $event.shop.shortTitle,
            }),
            fax: new ShopLocatorEvent({
                eventaction: "Phone Call",
                eventlabel: $event.shop.shortTitle,
            }),
            appointment_in_store_page: new ShopLocatorEvent({
                eventaction: "Take appointment",
                eventlabel: $event.shop.shortTitle,
            }),
            shop_page: new PromotionClickShopLocatorEvent({
                store: {
                    name: $event.shop.shortTitle,
                    position: $event.shopIndex,
                },
            }),
        };

        const event = eventMapping[$event.event];
        if (event) {
            this.analyticsService.sendData<
                GTMEventPromotionData | GTMEventData
            >(event);
        }
    }

    private manageAnalytics(): void {
        this.shopLocatorAnalyticsService.sendShopLocatorPageViewEvent();
        this.subscription.add(
            this.shopLocatorFacade.newSearch$.subscribe(
                (location: SelectedLocationState) => {
                    this.analyticsService.sendData(
                        new ShopLocatorEvent({
                            eventaction: "Search a store",
                            eventlabel: (
                                location as SelectedLocationCoordinates
                            ).address,
                        }),
                    );
                },
            ),
        );
    }

    private areLocationsSimilarEnoughToNotTriggerSubscription(
        previous: CurrentShopLocation,
        current: CurrentShopLocation,
    ): boolean {
        if (previous.pageMode !== current.pageMode) {
            return false;
        }

        if (
            "currentCountry" in current &&
            "currentCountry" in previous &&
            previous.currentCountry.countryCode !==
                current.currentCountry.countryCode
        ) {
            return false;
        }

        const hasCurrentCityChanged =
            "currentCity" in current &&
            "currentCity" in previous &&
            previous.currentCity.slugifiedCity !==
                current.currentCity.slugifiedCity;
        return !hasCurrentCityChanged;
    }

    private managePageTitle(currentLocation: CurrentShopLocation): void {
        switch (currentLocation.pageMode) {
            case SHOP_LOCATIONS_COUNTRY_MODE: {
                this.pageTitle = $localize`:@@hermes.store_locator.page_country_title:Our Hermes stores in ${currentLocation.currentCountry.translatedCountry}:INTERPOLATION:`;
                break;
            }
            case SHOP_LOCATIONS_CITY_MODE: {
                this.pageTitle = $localize`:@@hermes.store_locator.page_city_title:Our Hermès stores in ${currentLocation.currentCity.translatedCity}:INTERPOLATION: ${currentLocation.currentCountry.translatedCountry}:INTERPOLATION_1:`;
                break;
            }
            default: {
                this.pageTitle = $localize`:@@hermes.store_locator.page_title:Find a store`;
                break;
            }
        }
    }
}
